"use client";

import { VidMuseEvent } from "@/common/events";
import { LoggerEventParams, logger } from "@/common/logger";
import { ReactElement, useEffect, useRef } from "react";

interface ExposureTrackerProps {
  children: React.ReactNode;
  event: VidMuseEvent;
  params?: LoggerEventParams;
  threshold?: number;
  once?: boolean; // Whether to track only once per mount
}

export function ExposureTracker({
  children,
  event,
  params,
  threshold = 0.5,
  once = true,
}: ExposureTrackerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasTrackedRef = useRef(false);

  // Store the latest params in a ref to keep it available in the intersection observer
  // without having to re-run the effect when params (which might be an unstable object) changes.
  const paramsRef = useRef(params);
  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    if (once && hasTrackedRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (once && hasTrackedRef.current) return;

            logger.event(event, paramsRef.current);
            hasTrackedRef.current = true;

            if (once) {
              observer.unobserve(element);
            }
          }
        });
      },
      {
        threshold,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [event, threshold, once]);

  // If children is null/undefined/false, don't render anything and don't track
  if (children == null || children === false) {
    return null;
  }

  if (
    (children as ReactElement<{ visible?: boolean }>).props?.visible === false
  ) {
    return null;
  }

  return <div ref={containerRef}>{children}</div>;
}
